import { useState, type FC } from "react";

import { ForeignNationalDocumentsPanel } from "../../foreignNationalDocuments/components/ForeignNationalDocumentsPanel";
import type { ForeignNationalDocument } from "../../foreignNationalDocuments/types";
import { useClientProfile } from "../context/ClientProfileContext";
import { NAV_ITEMS } from "../navConfig";
import { RequestSignatureDialog } from "../signing/RequestSignatureDialog";
import type { ClientTab } from "../urlState";
import { ActivityTab } from "./activity/ActivityTab";
import { AddressesTab } from "./addresses/AddressesTab";
import { CasesTab } from "./cases/CasesTab";
import { DependentsTab } from "./dependents/DependentsTab";
import { EducationTab } from "./education/EducationTab";
import { EmploymentTab } from "./employment/EmploymentTab";
import { ImmigrationTab } from "./immigration/ImmigrationTab";
import { NotesTimeline } from "./notes/NotesTimeline";
import { OverviewTab } from "./overview/OverviewTab";
import { PassportsTab } from "./passports/PassportsTab";
import { PersonalTab } from "./personal/PersonalTab";
import { RemindersTab } from "./reminders/RemindersTab";
import { SettingsTab } from "./settings/SettingsTab";

const DocumentsSection: FC = () => {
  const { clientId, client, url } = useClientProfile();
  const [signing, setSigning] = useState<ForeignNationalDocument | null>(null);
  const first = (client?.first_name ?? "").trim() || "the client";
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ForeignNationalDocumentsPanel
        foreignNationalId={clientId}
        title={NAV_ITEMS.documents.label}
        tableShell="embedded"
        clientFirstName={client?.first_name ?? null}
        onCreateRequest={(opts) => url.openCreateIntake(opts)}
        focusDocumentId={url.state.focus.documentId ?? null}
        onRequestSignature={{
          label: `Ask ${first} to sign`,
          onRequest: setSigning
        }}
      />
      <RequestSignatureDialog
        clientId={clientId}
        firstName={first}
        document={signing}
        onOpenChange={(open) => {
          if (!open) setSigning(null);
        }}
      />
    </div>
  );
};

const ActiveSection: FC<{ tab: ClientTab }> = ({ tab: activeTab }) => {
  switch (activeTab) {
    case "overview":
      return <OverviewTab />;
    case "cases":
      return <CasesTab />;
    case "personal":
      return <PersonalTab />;
    case "immigration_overview":
      return <ImmigrationTab />;
    case "passports":
      return <PassportsTab />;
    case "education":
      return <EducationTab />;
    case "address_history":
      return <AddressesTab />;
    case "employment":
      return <EmploymentTab />;
    case "documents":
      return <DocumentsSection />;
    case "outgoing_intakes":
      // Rendered by the client workspace (the Requests center).
      return null;
    case "dependents":
      return <DependentsTab />;
    case "reminders":
      return <RemindersTab />;
    case "activity":
      return <ActivityTab />;
    case "notes":
      return <NotesTimeline />;
    case "settings":
      return <SettingsTab />;
    default:
      return null;
  }
};

/**
 * Sections that read as one rounded card in Comfortable (a header band over a
 * list, table or calendar). The rest (the Overview dashboard, the forms and the
 * Notes timeline) are a flow of cards on the tinted page. See
 * styles/client-profile-cards.css; Compact ignores both (`display: contents`).
 */
const FLOW_TABS: ReadonlySet<ClientTab> = new Set<ClientTab>([
  "overview",
  "personal",
  "notes",
  "settings"
]);

/** Renders the active section inside the `client-tab` shell. */
export const ClientTabPanel: FC = () => {
  const { activeTab } = useClientProfile();
  return (
    <div
      className="client-tab contents"
      data-layout={FLOW_TABS.has(activeTab) ? "flow" : "card"}
      data-tab={activeTab}
    >
      <ActiveSection tab={activeTab} />
    </div>
  );
};
